/**
 * OnboardingWizardController — pilote /bienvenue.html (EPIC-068 / 68.2, §15.A).
 *
 * Gate d'onboarding prospect, parcours conditionnel :
 *   1. has-ME — « Avez-vous déjà une micro-entreprise ? » : Oui / Non
 *      → PATCH { prospectHasExistingMicro: true|false }.
 *      « Passer pour l'instant » → PATCH { prospectHasExistingMicro: null }
 *      puis redirection dashboard (persistance du skip).
 *   2. SIRET (branche « Oui » uniquement) — étape optionnelle : le prospect
 *      peut renseigner son SIRET pour personnaliser ses outils. On interroge
 *      l'annuaire public (recherche-entreprises) pour pré-remplir prénom/nom
 *      à l'étape suivante, et on persiste le SIRET déclaré
 *      (PATCH { prospectSiret }). Strictement non bloquant.
 *   3. prénom / nom — submit → PATCH { firstName, usageLastName } puis
 *      redirection dashboard.
 *
 * Le parcours actif est une séquence d'index de panels (`_flow`) :
 *   - « Oui »      → [has-ME, SIRET, nom]   (3 étapes)
 *   - « Non »/skip → [has-ME, nom]          (2 étapes)
 * Le libellé de progression s'adapte à la longueur du parcours.
 *
 * Aucune construction HTML inline : les panels sont statiques, togglés via la
 * classe `is-active` (cf. `.nm-wizard__panel` dans notamicro.css).
 *
 * Accessibilité (WCAG 2.1 AA) : le focus est déplacé sur le titre de la
 * nouvelle étape à chaque transition ; pas de rechargement de page.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { findEstablishmentBySiret, derivePersonName } from "/js/services/entreprises.js";

const DASHBOARD_URL = "/tableau-de-bord-prospect.html";

// Index des panels dans le DOM (attribut data-wizard-panel).
const PANEL_HAS_MICRO = 0;
const PANEL_SIRET = 1;
const PANEL_NAME = 2;

export default class OnboardingWizardController extends BaseController {
  static targets = [
    "panel",
    "progress",
    "hasMicroBtn",
    "hasMicroError",
    "siretChoice",
    "siretInputField",
    "siretInput",
    "siretError",
    "siretContinueBtn",
    "firstNameInput",
    "lastNameInput",
    "nameError",
    "submitBtn",
  ];

  // Parcours actif (séquence d'index de panels) + position courante. Par
  // défaut : has-ME → nom (la réponse « oui » y insère l'étape SIRET).
  _flow = [PANEL_HAS_MICRO, PANEL_NAME];
  _pos = 0;

  connect() {
    // On ne vole pas le focus au montage (l'utilisateur arrive en haut de page).
    this._goTo(0, { focus: false });
  }

  // ---------------------------------------------------------------------------
  // Navigation entre panels (par position dans le parcours actif)
  // ---------------------------------------------------------------------------

  _goTo(pos, { focus = true } = {}) {
    if (pos < 0 || pos >= this._flow.length) return;
    this._pos = pos;
    const panelIndex = this._flow[pos];
    this.panelTargets.forEach((panel) => {
      const i = Number.parseInt(panel.dataset.wizardPanel, 10);
      panel.classList.toggle("is-active", i === panelIndex);
    });
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `Étape ${pos + 1} sur ${this._flow.length}`;
    }
    if (focus) {
      const panel = this.panelTargets.find(
        (p) => Number.parseInt(p.dataset.wizardPanel, 10) === panelIndex,
      );
      const heading = panel ? panel.querySelector(".nm-wizard__panel-h") : null;
      if (heading) heading.focus({ preventScroll: false });
    }
  }

  _goToNameStep() {
    const pos = this._flow.indexOf(PANEL_NAME);
    this._goTo(pos >= 0 ? pos : this._flow.length - 1);
  }

  // ---------------------------------------------------------------------------
  // Étape 1 — has-ME
  // ---------------------------------------------------------------------------

  async answerHasMicro(event) {
    const value = event.params.hasMicro === true;
    this._setHasMicroBusy(true);
    this._hideHasMicroError();
    try {
      await api.updateOnboarding({ prospectHasExistingMicro: value });
      // « Oui » insère l'étape SIRET optionnelle avant le nom ; « non » va au nom.
      this._flow = value
        ? [PANEL_HAS_MICRO, PANEL_SIRET, PANEL_NAME]
        : [PANEL_HAS_MICRO, PANEL_NAME];
      this._goTo(1);
    } catch (_e) {
      // 401 → /connexion.html déjà géré par api.js.
      this._showHasMicroError("Impossible d'enregistrer votre réponse. Veuillez réessayer.");
    } finally {
      this._setHasMicroBusy(false);
    }
  }

  async skip() {
    this._setHasMicroBusy(true);
    this._hideHasMicroError();
    try {
      await api.updateOnboarding({ prospectHasExistingMicro: null });
      window.location.assign(DASHBOARD_URL);
    } catch (_e) {
      this._showHasMicroError("Impossible d'enregistrer votre choix. Veuillez réessayer.");
      this._setHasMicroBusy(false);
    }
  }

  _setHasMicroBusy(busy) {
    this.hasMicroBtnTargets.forEach((btn) => {
      btn.disabled = busy;
    });
  }

  _showHasMicroError(message) {
    if (!this.hasHasMicroErrorTarget) return;
    this.hasMicroErrorTarget.textContent = message;
    this.hasMicroErrorTarget.hidden = false;
  }

  _hideHasMicroError() {
    if (!this.hasHasMicroErrorTarget) return;
    this.hasMicroErrorTarget.hidden = true;
    this.hasMicroErrorTarget.textContent = "";
  }

  // ---------------------------------------------------------------------------
  // Étape SIRET (branche « Oui ») — optionnelle, non bloquante
  // ---------------------------------------------------------------------------

  // Le radio « Je le renseigne… » est coché par défaut (champ visible) ; choisir
  // « Je ne souhaite pas… » masque le champ.
  toggleSiretInput() {
    const provide = this._wantsToProvideSiret();
    if (this.hasSiretInputFieldTarget) this.siretInputFieldTarget.hidden = !provide;
    this._hideSiretError();
    if (provide && this.hasSiretInputTarget) this.siretInputTarget.focus();
  }

  _wantsToProvideSiret() {
    const chosen = this.siretChoiceTargets.find((radio) => radio.checked);
    // « provide » par défaut si aucun radio n'est explicitement coché.
    return chosen ? chosen.value === "provide" : true;
  }

  async submitSiret(event) {
    event?.preventDefault?.();
    this._hideSiretError();

    // Choix « ne souhaite pas » → on passe directement au nom.
    if (!this._wantsToProvideSiret()) {
      this._goToNameStep();
      return;
    }

    const digits = (this.hasSiretInputTarget ? this.siretInputTarget.value : "").replace(/\D/g, "");

    // Champ laissé vide alors que « je le renseigne » est coché → on n'insiste pas.
    if (digits === "") {
      this._goToNameStep();
      return;
    }
    if (digits.length !== 14) {
      this._showSiretError("Le SIRET doit comporter 14 chiffres.");
      if (this.hasSiretInputTarget) this.siretInputTarget.focus();
      return;
    }

    this._setSiretBusy(true);
    try {
      // Annuaire public : pré-remplissage best-effort du prénom/nom (non bloquant).
      const result = await findEstablishmentBySiret(digits, { include: "siege,dirigeants" });
      if (result) {
        const { firstName, lastName } = derivePersonName(result);
        if (firstName && this.hasFirstNameInputTarget && !this.firstNameInputTarget.value) {
          this.firstNameInputTarget.value = firstName;
        }
        if (lastName && this.hasLastNameInputTarget && !this.lastNameInputTarget.value) {
          this.lastNameInputTarget.value = lastName;
        }
      }
      // Persistance du SIRET déclaré, même si l'annuaire n'a rien renvoyé.
      try {
        await api.updateOnboarding({ prospectSiret: digits });
      } catch (_e) {
        // Échec d'enregistrement non bloquant : on n'empêche pas l'onboarding.
      }
      this._goToNameStep();
    } finally {
      this._setSiretBusy(false);
    }
  }

  _setSiretBusy(busy) {
    if (!this.hasSiretContinueBtnTarget) return;
    this.siretContinueBtnTarget.disabled = busy;
    this.siretContinueBtnTarget.textContent = busy ? "Un instant…" : "Continuer →";
  }

  _showSiretError(message) {
    if (!this.hasSiretErrorTarget) return;
    this.siretErrorTarget.textContent = message;
    this.siretErrorTarget.hidden = false;
  }

  _hideSiretError() {
    if (!this.hasSiretErrorTarget) return;
    this.siretErrorTarget.hidden = true;
    this.siretErrorTarget.textContent = "";
  }

  // ---------------------------------------------------------------------------
  // Étape finale — prénom / nom
  // ---------------------------------------------------------------------------

  async submitName(event) {
    event.preventDefault();
    const firstName = this.hasFirstNameInputTarget ? this.firstNameInputTarget.value.trim() : "";
    const usageLastName = this.hasLastNameInputTarget ? this.lastNameInputTarget.value.trim() : "";

    if (!firstName || !usageLastName) {
      this._showNameError("Merci d'indiquer votre prénom et votre nom.");
      if (!firstName && this.hasFirstNameInputTarget) {
        this.firstNameInputTarget.focus();
      } else if (!usageLastName && this.hasLastNameInputTarget) {
        this.lastNameInputTarget.focus();
      }
      return;
    }

    this._hideNameError();
    this._setSubmitBusy(true);
    try {
      await api.updateOnboarding({ firstName, usageLastName });
      window.location.assign(DASHBOARD_URL);
    } catch (_e) {
      this._showNameError("Impossible d'enregistrer. Veuillez réessayer.");
      this._setSubmitBusy(false);
    }
  }

  _setSubmitBusy(busy) {
    if (!this.hasSubmitBtnTarget) return;
    this.submitBtnTarget.disabled = busy;
    this.submitBtnTarget.textContent = busy ? "Enregistrement…" : "Terminer →";
  }

  _showNameError(message) {
    if (!this.hasNameErrorTarget) return;
    this.nameErrorTarget.textContent = message;
    this.nameErrorTarget.hidden = false;
  }

  _hideNameError() {
    if (!this.hasNameErrorTarget) return;
    this.nameErrorTarget.hidden = true;
    this.nameErrorTarget.textContent = "";
  }
}
